import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ViralStrategySelector.css';

/**
 * ViralStrategySelector Component
 *
 * Allows users to choose from 8 pre-configured viral reward strategies
 * or customize their own viral mechanics.
 */
function ViralStrategySelector({ selectedStrategy, onChange }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('http://localhost:3001/api/viral-strategies/templates');
      setTemplates(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching viral strategy templates:', error);
      setLoading(false);
    }
  };

  const selectTemplate = (template) => {
    onChange({
      ...template.config,
      template_id: template.id,
      template_name: template.name
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      growth: '#28a745',
      revenue: '#007bff',
      loyalty: '#6f42c1',
      vip: '#fd7e14'
    };
    return colors[category] || '#6c757d';
  };

  const getCategoryLabel = (category) => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  if (loading) {
    return (
      <div className="viral-strategy-selector loading">
        <div className="spinner"></div>
        <p>Loading viral strategies...</p>
      </div>
    );
  }

  return (
    <div className="viral-strategy-selector">
      <div className="viral-strategy-header">
        <h3>🚀 Viral Reward Strategy</h3>
        <p className="viral-strategy-description">
          Choose how participants earn rewards for referring friends. Each strategy has different growth
          characteristics, fairness trade-offs, and revenue implications.
        </p>
      </div>

      <div className="strategy-grid">
        {templates.map((template) => (
          <div
            key={template.id}
            className={`strategy-card ${
              selectedStrategy?.template_id === template.id ? 'selected' : ''
            }`}
            onClick={() => selectTemplate(template)}
          >
            <div className="strategy-card-header">
              <span className="strategy-icon">{template.icon}</span>
              <span
                className="strategy-category"
                style={{ background: getCategoryColor(template.category) }}
              >
                {getCategoryLabel(template.category)}
              </span>
            </div>

            <h4 className="strategy-name">{template.name}</h4>
            <p className="strategy-description">{template.description}</p>

            <div className="strategy-recommended">
              <strong>Best For:</strong>
              <p>{template.recommended_for}</p>
            </div>

            {selectedStrategy?.template_id === template.id && (
              <div className="strategy-selected-badge">
                ✓ Selected
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedStrategy && (
        <div className="selected-strategy-details">
          <button
            type="button"
            className="btn-toggle-details"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? '▼' : '▶'} {showDetails ? 'Hide' : 'Show'} Strategy Configuration
          </button>

          {showDetails && (
            <div className="strategy-config-display">
              <h4>Current Configuration:</h4>
              <div className="config-grid">
                <div className="config-item">
                  <label>Strategy Type:</label>
                  <span className="config-value">{selectedStrategy.strategy_type}</span>
                </div>

                {selectedStrategy.strategy_type === 'same_raffle' && (
                  <>
                    <div className="config-item">
                      <label>Tickets Per Referral:</label>
                      <span className="config-value">{selectedStrategy.tickets_per_referral || 1}</span>
                    </div>
                    <div className="config-item">
                      <label>Smart Caps Enabled:</label>
                      <span className="config-value">{selectedStrategy.enable_smart_caps ? 'Yes' : 'No'}</span>
                    </div>
                    {selectedStrategy.max_bonus_tickets && (
                      <div className="config-item">
                        <label>Max Bonus Tickets:</label>
                        <span className="config-value">{selectedStrategy.max_bonus_tickets}</span>
                      </div>
                    )}
                  </>
                )}

                {selectedStrategy.strategy_type === 'bonus_raffle' && (
                  <>
                    <div className="config-item">
                      <label>Bonus Prize:</label>
                      <span className="config-value">{selectedStrategy.bonus_prize_name || 'Separate Drawing'}</span>
                    </div>
                    <div className="config-item">
                      <label>Bonus Tickets Per Referral:</label>
                      <span className="config-value">{selectedStrategy.bonus_tickets_per_referral || 1}</span>
                    </div>
                  </>
                )}

                {selectedStrategy.strategy_type === 'credits' && (
                  <>
                    <div className="config-item">
                      <label>Credits Per Referral:</label>
                      <span className="config-value">${selectedStrategy.credits_per_referral || 0}</span>
                    </div>
                    {selectedStrategy.max_credits && (
                      <div className="config-item">
                        <label>Max Credits Cap:</label>
                        <span className="config-value">${selectedStrategy.max_credits}</span>
                      </div>
                    )}
                  </>
                )}

                {selectedStrategy.strategy_type === 'cash' && (
                  <>
                    <div className="config-item">
                      <label>Cash Per Referral:</label>
                      <span className="config-value">${selectedStrategy.cash_per_referral || 0}</span>
                    </div>
                    <div className="config-item">
                      <label>Payout Method:</label>
                      <span className="config-value">{selectedStrategy.cash_payout_method || 'PayPal'}</span>
                    </div>
                  </>
                )}

                {selectedStrategy.strategy_type === 'discount' && (
                  <>
                    <div className="config-item">
                      <label>Discount Percentage:</label>
                      <span className="config-value">{selectedStrategy.discount_percentage || 0}%</span>
                    </div>
                    <div className="config-item">
                      <label>Min Referrals Required:</label>
                      <span className="config-value">{selectedStrategy.min_referrals_for_discount || 1}</span>
                    </div>
                  </>
                )}

                <div className="config-item">
                  <label>Social Sharing:</label>
                  <span className="config-value">{selectedStrategy.enable_social_sharing ? 'Enabled' : 'Disabled'}</span>
                </div>
                <div className="config-item">
                  <label>Leaderboard:</label>
                  <span className="config-value">{selectedStrategy.enable_leaderboard ? 'Enabled' : 'Disabled'}</span>
                </div>
                <div className="config-item">
                  <label>Social Proof:</label>
                  <span className="config-value">{selectedStrategy.enable_social_proof ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!selectedStrategy && (
        <div className="no-strategy-selected">
          <p>💡 Optional: Select a viral strategy to boost referrals, or skip to continue</p>
        </div>
      )}
    </div>
  );
}

export default ViralStrategySelector;
